/* eslint-disable max-lines */
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRoute } from 'react-router-dom-v5-compat';
import { Center, Spinner } from 'src/components/atoms';
import { SubmissionTypes } from 'src/components/pages';
import { AppDispatch } from 'src/store';
import OPRLoginPage from 'src/components/public/Login/OPRLoginPage';
import OPRResetPage from 'src/components/public/Login/OPRResetPage';
import MessageSettings from 'src/components/public/MessageSettings/MessageSettings';
import { OPRForgotPassword } from 'src/components/public/OPRForgotPassword/OPRForgotPassword';
import OPRLandingPage from 'src/components/public/OPRLandingPage/OPRLandingPage';
import { OPRLoadingPage } from 'src/components/public/OPRLoadingPage/OPRLoadingPage';
import { PublicMoreEvidence } from 'src/components/public/PublicMoreEvidence';
import PublicReportsList from 'src/components/public/PublicReportsList';
import CountryQuestion from 'src/components/public/questions/CountryQuestion';
import { EvidenceQuestion } from 'src/components/public/questions/EvidenceQuestion/EvidenceQuestion';
import SiteQuestion from 'src/components/public/questions/SiteQuestion';
import { closeSocket, emitTranslationInfo } from 'src/creators/socket';
import {
  useGetGlobalFeaturesQuery,
  useGetPrivacyStatementQuery,
  useGetPublicCountriesQuery,
  useGetPublicFeaturesQuery,
  useGetPublicSitesQuery,
} from 'src/graphql';
import * as Types from 'src/types';
import {
  trackChatTranslationUpdatedEvent,
  TRANSLATION_UPDATE_SOURCE,
} from 'src/utils/analytics/track/messagesTranslation';
import { userHash } from 'src/utils/analytics/utils/userHash';
import { useTheme } from 'styled-components';
import { EvidenceType, PermissionsType } from '../../../../api';
import {
  connectSocket,
  fetchPublicReports,
  setEmail,
  setEventDate,
  setEventTime,
  setName,
  setOpenReportQuestion,
  setOrganisation,
  setOrganisationPrivacyStatement,
  setSelectedCountry,
  setSelectedSite,
} from '../../../../creators/OpenReporting.creators';
import i18next from '../../../../i18n';
import { unreadOpenMessages } from '../../../../selectors';
import { OpenReportTypes, State, User, USER_ROLE } from '../../../../types';
import PageNotFound from '../../../PageNotFound';
import { AuthenticatedRoute, isRequiredFeatureEnabled } from '../../../auth';
import ChatContainer from '../../../messages/ChatContainer';
import { RelationshipTypes } from '../../../organismsLegacy/RelationshipTypes';
import ChangePassword from '../../../public/Password/ChangePassword';
import SubmitReport from '../../../public/Password/SubmitReport';
import PublicMessageList from '../../../public/PublicMessageList';
import PublicReport from '../../../public/PublicReport';
import SubmitSuccess from '../../../public/SubmitSuccess';
import DateQuestion from '../../../public/questions/DateQuestion';
import TextQuestion from '../../../public/questions/TextQuestion';
import TimeQuestion from '../../../public/questions/TimeQuestion';
import { Details } from '../../Details';
import { PublicRoutingBackground } from './PublicRouting.styles';
import { ReportViewProvider } from './components/ReportViewProvider';
import { SubmissionFormProvider } from './components/SubmissionFormProvider';
import { submissionPaths } from './config';
import {
  getOrganisationResponse,
  onMessageSend,
  onReadMessages,
} from './helpers';

export const hasPublicAccess = (user: User): boolean => {
  if (user.permissions.includes(PermissionsType.PUBLIC)) {
    return true;
  }
  return false;
};

export const PublicRouting = ({ organisation }: { organisation: string }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const translateMessages = useSelector(
    (state: State) =>
      state.openReporting?.openReportingReducer?.translateMessages,
  );

  const publicReports = useSelector(
    (state: State) => state.openReporting?.openReportingReducer?.publicReports,
  );
  const messages =
    useSelector(
      (state: State) => state.openReporting?.openReportingReducer?.messages,
    ) ?? new Map();
  const unreadMessages = useSelector(
    (state: State) => unreadOpenMessages(state) !== 0,
  );

  const session =
    useSelector(
      (state: State) => state.openReporting?.openReportingReducer?.session,
    ) ?? '';

  const logo =
    useSelector(
      (state: State) => state.openReporting?.openReportReducer?.logo,
    ) ?? '';

  const openReporting = useSelector(
    (state: State) => ({
      changePasswordValues: state.openReporting.changePasswordReducer,
      publicReport: state.openReporting.openReportReducer,
      ...state.openReporting?.openReportingReducer,
    }),
    shallowEqual,
  );

  const reportIsSubmitting =
    useSelector(
      (state: State) => state.openReporting?.openReportingReducer?.isSubmitting,
    ) ?? false;

  const dispatch = useDispatch<AppDispatch>();

  const { data: featuresData } = useGetGlobalFeaturesQuery();

  const { data: publicFeatures } = useGetPublicFeaturesQuery({
    slug: organisation,
  });

  const { data: privacyStatementUrl } = useGetPrivacyStatementQuery({
    slug: organisation ?? '',
  });

  const globalFlags =
    (featuresData?.getGlobalFeatures as Types.Feature[]) ?? [];
  const publicFlags =
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [];

  const isDynamicTranslationsEnabled = isRequiredFeatureEnabled(
    [...publicFlags, ...globalFlags],
    Types.FeatureFlag.DYNAMIC_TRANSLATIONS,
  );
  const isReporterViewEvidenceDisabled = isRequiredFeatureEnabled(
    publicFlags,
    Types.FeatureFlag.REPORTER_VIEW_EVIDENCE_DISABLED,
  );

  const segmentEnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const isAnonymousEnabledForOrg = isRequiredFeatureEnabled(
    publicFlags,
    Types.FeatureFlag.ANONYMOUS,
  );

  const isOprPasswordResetEnabled = isRequiredFeatureEnabled(
    [...globalFlags, ...publicFlags],
    Types.FeatureFlag.OPR_PASSWORD_RESET,
  );

  const isOprCountryEnabled = isRequiredFeatureEnabled(
    publicFlags,
    Types.FeatureFlag.OPR_COUNTRY,
  );

  const isOprSiteEnabled = isRequiredFeatureEnabled(
    publicFlags,
    Types.FeatureFlag.OPR_SITE,
  );

  const { data: publicCountriesData } = useGetPublicCountriesQuery(
    { slug: organisation },
    { enabled: isOprCountryEnabled },
  );

  const countries = (publicCountriesData?.getPublicCountries ?? []).map(
    ({ name }) => ({ name }),
  );

  const { data: publicSitesData } = useGetPublicSitesQuery(
    { slug: organisation },
    { enabled: isOprSiteEnabled },
  );

  const sites = (publicSitesData?.getPublicSites ?? []).map(({ name }) => ({
    name,
  }));

  useEffect(() => {
    dispatch(setOrganisation(organisation));
    getOrganisationResponse(organisation, dispatch, setNotFound);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      setOrganisationPrivacyStatement(privacyStatementUrl?.getPrivacyStatement),
    );
  }, [privacyStatementUrl, dispatch]);

  useEffect(() => {
    const user = openReporting.publicReporterAccount?.user;
    if (user) {
      dispatch({
        type: OpenReportTypes.TRANSLATE_MESSAGES,
        payload: user.translationEnabled,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReporting.publicReporterAccount]);

  const updateTranslateMessages = async (translate: boolean) => {
    dispatch({
      type: OpenReportTypes.TRANSLATE_MESSAGES,
      payload: translate,
    });

    dispatch(fetchPublicReports() as unknown as Parameters<AppDispatch>[0]);
    emitTranslationInfo({ translate });
    await closeSocket()();
    await connectSocket(session, dispatch);

    if (segmentEnabled) {
      const currentReport = publicReports?.[0];
      const trackChatTranslationUpdatedEventProps = {
        organisation_id:
          openReporting.publicReporterAccount?.user?.organisation?.id,
        user_id: await userHash(openReporting.publicReporterAccount?.user?.id),
        user_role: USER_ROLE.REPORTER,
        source: TRANSLATION_UPDATE_SOURCE.OPR,
        report_id: currentReport?.reportId,
        translated: translate,
        language: translate ? i18next.language : undefined,
        reported_category: currentReport?.reportedCategory?.name,
        determined_category: currentReport?.determinedCategory?.name,
      };
      trackChatTranslationUpdatedEvent(trackChatTranslationUpdatedEventProps);
    }
  };

  const theme = useTheme();
  const updateTranslationEnabled = async (translationEnabled: boolean) => {
    dispatch({
      type: OpenReportTypes.SET_TRANSLATION_ENABLED,
      payload: translationEnabled,
    });
    updateTranslateMessages(translationEnabled);
  };

  const currentUser = openReporting.publicReporterAccount?.user;

  const location = useLocation();

  const formSteps = useMemo(
    () => [
      {
        key: 'relationship',
        required: true,
        value: openReporting.publicReport?.relationshipToCompany,
      },
      {
        key: 'submission-type',
        required: true,
        value: openReporting.publicReport?.evidence.report.category,
      },
      {
        key: 'perpetrators',
        required: false,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/perpetrator',
        ),
      },
      {
        key: 'location',
        required: false,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/location',
        ),
      },
      {
        key: 'date',
        required: true,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/date',
        ),
      },
      {
        key: 'time',
        required: true,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/time',
        ),
      },
      {
        key: 'description',
        required: true,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/description',
        ),
      },
      {
        key: 'question',
        required: false,
        value: openReporting.challenge?.challenge,
      },
      {
        key: 'emotion',
        required: false,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/emotion',
        ),
      },
      {
        key: 'witnesses',
        required: false,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/witness',
        ),
      },
      {
        key: 'file-upload',
        required: false,
        value: '',
      },
      {
        key: 'details',
        required: false,
        value: openReporting.publicReport?.evidence?.evidence.find(
          (e) => e.type === '@evidence/details',
        ),
      },
      {
        key: 'password',
        required: true,
        value: openReporting.publicReport?.password,
      },
      {
        key: 'success',
        required: false,
        value: openReporting.publicReport?.confirmation,
      },
    ],
    [openReporting],
  );

  const currentPath = location.pathname;
  const currentStepIndex = formSteps.findIndex((step) =>
    currentPath.includes(`/open-reporting/${step.key}`),
  );

  const isFormRoute = currentStepIndex !== -1;
  const isNotSuccessPage = !currentPath.includes(`/open-reporting/success`);

  if (isFormRoute && isNotSuccessPage && !openReporting.reportJustSubmitted) {
    const priorRequiredSteps = formSteps
      .slice(0, currentStepIndex)
      .filter((step) => step.required);

    const anyMissing = priorRequiredSteps.some(
      (step) => step.value === null || step.value === '',
    );

    if (anyMissing) {
      return <Redirect to={`/${organisation}/open-reporting`} />;
    }
  }

  if (loading) {
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  if (reportIsSubmitting) {
    return <OPRLoadingPage organisation={organisation} />;
  }

  if (notFound) {
    return (
      <PageNotFound
        color={
          theme.isVaultVariant
            ? theme.newPalette.turquoise.T300
            : theme.atlas.semantic.color.action.primary.default
        }
      />
    );
  }

  return (
    <Switch>
      <Route exact path={submissionPaths}>
        <SubmissionFormProvider organisation={organisation}>
          <CompatRoute
            exact
            path={`/:slug/open-reporting`}
            render={() => (
              <>
                <OPRLandingPage
                  descriptionParagraphs={[
                    t('public.CreatePublicReport.reportIntroductionParagraph1'),
                    t('public.CreatePublicReport.reportIntroductionParagraph2'),
                    t('public.CreatePublicReport.reportIntroductionParagraph3'),
                  ]}
                />
              </>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/relationship`}
            render={() => (
              <PublicRoutingBackground>
                <RelationshipTypes
                  isCountryEnabled={isOprCountryEnabled}
                  isSiteEnabled={isOprSiteEnabled}
                />
              </PublicRoutingBackground>
            )}
          />

          {isOprCountryEnabled && (
            <Route
              exact
              path={`/:organisation/open-reporting/country`}
              render={() => (
                <PublicRoutingBackground>
                  <CountryQuestion
                    organisation={organisation}
                    countries={countries}
                    selectedCountryName={
                      openReporting.publicReport?.selectedCountryName ?? ''
                    }
                    onCountrySelected={(name) =>
                      dispatch(setSelectedCountry(name))
                    }
                    backLink="relationship"
                    nextLink={isOprSiteEnabled ? 'site' : 'submission-type'}
                    percentage={15}
                  />
                </PublicRoutingBackground>
              )}
            />
          )}

          {isOprSiteEnabled && (
            <Route
              exact
              path={`/:organisation/open-reporting/site`}
              render={() => (
                <PublicRoutingBackground>
                  <SiteQuestion
                    organisation={organisation}
                    sites={sites}
                    selectedSiteName={
                      openReporting.publicReport?.selectedSiteName ?? ''
                    }
                    onSiteSelected={(name) => dispatch(setSelectedSite(name))}
                    backLink={isOprCountryEnabled ? 'country' : 'relationship'}
                    nextLink="submission-type"
                    percentage={20}
                  />
                </PublicRoutingBackground>
              )}
            />
          )}

          <Route
            exact
            path={`/:organisation/open-reporting/submission-type`}
            render={() => (
              <PublicRoutingBackground>
                <SubmissionTypes
                  isCountryEnabled={isOprCountryEnabled}
                  isSiteEnabled={isOprSiteEnabled}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/perpetrators`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'perpetrators'}
                  organisation={organisation}
                  textFieldValue={openReporting.perpetrators ?? ''}
                  i18nHeader="public.Perpetrators.header"
                  i18nDescription="public.Perpetrators.description"
                  i18nPlaceholder="public.Perpetrators.placeholder"
                  backLink="file-upload"
                  nextLink="details"
                  percentage={85}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_PERPETRATORS,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/location`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'location'}
                  organisation={organisation}
                  textFieldValue={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.LOCATION,
                    )?.value as string) || ''
                  }
                  i18nHeader="public.Location.header"
                  i18nDescription="public.Location.description"
                  i18nPlaceholder="public.Location.placeholder"
                  backLink="time"
                  nextLink="description"
                  percentage={50}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_LOCATION,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/date`}
            render={() => (
              <PublicRoutingBackground>
                <DateQuestion
                  key={'date'}
                  organisation={organisation}
                  setEventDate={(data: string) => {
                    dispatch(setEventDate(data));
                  }}
                  date={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.DATE,
                    )?.value as string) || ''
                  }
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/time`}
            render={() => (
              <PublicRoutingBackground>
                <TimeQuestion
                  key={'time'}
                  organisation={organisation}
                  setEventTime={(time: string) => {
                    dispatch(setEventTime(time));
                  }}
                  time={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.TIME,
                    )?.value as string) || ''
                  }
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/description`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'description'}
                  organisation={organisation}
                  textFieldValue={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.DESCRIPTION,
                    )?.value as string) || ''
                  }
                  i18nHeader="public.Description.whatHappened"
                  i18nDescription="public.Description.whatHappenedText"
                  i18nPlaceholder="public.Description.whatHappenedPlaceholder"
                  backLink="location"
                  requireAnswer={true}
                  nextLink="emotion"
                  percentage={60}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_DESCRIPTION,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/question`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'question'}
                  organisation={organisation}
                  textFieldValue={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.DESCRIPTION,
                    )?.value as string) || ''
                  }
                  i18nHeader="public.Question.question"
                  i18nDescription="public.Question.questionText"
                  i18nPlaceholder="public.Question.placeholder"
                  backLink="submission-type"
                  requireAnswer={true}
                  nextLink="file-upload"
                  percentage={60}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_DESCRIPTION,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/emotion`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'emotion'}
                  organisation={organisation}
                  textFieldValue={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.EMOTION,
                    )?.value as string) || ''
                  }
                  i18nHeader="public.Emotion.header"
                  i18nDescription="public.Emotion.description"
                  i18nPlaceholder="public.Emotion.placeholder"
                  backLink="description"
                  nextLink="witnesses"
                  percentage={70}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_EMOTION,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/witnesses`}
            render={() => (
              <PublicRoutingBackground>
                <TextQuestion
                  key={'witnesses'}
                  organisation={organisation}
                  textFieldValue={
                    (openReporting.publicReport?.evidence.evidence.find(
                      (evidence) => evidence.type === EvidenceType.WITNESS,
                    )?.value as string) || ''
                  }
                  i18nHeader="public.Witnesses.header"
                  i18nDescription="public.Witnesses.description"
                  i18nPlaceholder="public.Witnesses.placeholder"
                  backLink="emotion"
                  nextLink="file-upload"
                  percentage={80}
                  setQuestionData={(text: string) => {
                    dispatch(
                      setOpenReportQuestion(
                        OpenReportTypes.SET_EVENT_WITNESSES,
                        text,
                      ),
                    );
                  }}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/file-upload`}
            render={() => (
              <PublicRoutingBackground>
                <EvidenceQuestion
                  key={'file-upload'}
                  organisation={organisation}
                  i18nHeader="public.FileUpload.header"
                  i18nDescription="public.FileUpload.description"
                  publicReport={openReporting.publicReport!}
                  percentage={85}
                />
              </PublicRoutingBackground>
            )}
          />

          <Route
            exact
            path={`/:organisation/open-reporting/details`}
            render={() => (
              <PublicRoutingBackground>
                <Details
                  organisation={organisation}
                  publicReport={openReporting.publicReport!}
                  setEmail={(
                    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                  ) => {
                    dispatch(setEmail(event));
                  }}
                  setName={(
                    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                  ) => {
                    dispatch(setName(event));
                  }}
                  isAnonymousEnabledForOrg={isAnonymousEnabledForOrg}
                />
              </PublicRoutingBackground>
            )}
          />
          <Route
            exact
            path={`/:organisation/open-reporting/password`}
            render={() => (
              <PublicRoutingBackground>
                <SubmitReport />
              </PublicRoutingBackground>
            )}
          />
          <Route
            exact
            path={`/:organisation/open-reporting/success`}
            render={() => (
              <PublicRoutingBackground>
                <SubmitSuccess
                  organisation={organisation}
                  publicReporterAccount={openReporting.publicReporterAccount!}
                  currentUser={currentUser as unknown as User}
                  currentReport={publicReports?.[0] ?? null}
                />
              </PublicRoutingBackground>
            )}
          />
        </SubmissionFormProvider>
      </Route>

      {isOprPasswordResetEnabled && (
        <CompatRoute
          exact
          path={`/:organisation/open-reporting/reset`}
          render={() => (
            <PublicRoutingBackground>
              <OPRForgotPassword organisation={organisation} logo={logo} />
            </PublicRoutingBackground>
          )}
        />
      )}

      <CompatRoute
        exact
        path={`/:slug/open-reporting/login`}
        render={() => (
          <PublicRoutingBackground>
            <OPRLoginPage />
          </PublicRoutingBackground>
        )}
      />

      <CompatRoute
        exact
        path={`/:slug/open-reporting/reset/:token`}
        render={() => (
          <PublicRoutingBackground>
            <OPRResetPage
              isOprPasswordResetEnabled={isOprPasswordResetEnabled}
            />
          </PublicRoutingBackground>
        )}
      />

      <AuthenticatedRoute>
        <Route
          exact
          path={`/:organisation/open-reporting/reports`}
          render={() => (
            <PublicRoutingBackground>
              <PublicReportsList
                organisation={organisation}
                currentUser={currentUser as unknown as User}
              />
            </PublicRoutingBackground>
          )}
        />
        <Route
          exact
          path={`/:organisation/open-reporting/reports/:id`}
          render={(props) => (
            <PublicRoutingBackground>
              <PublicReport
                organisation={organisation}
                reportId={props.match.params.id}
                currentUser={currentUser as unknown as User}
                isReporterViewEvidenceDisabled={isReporterViewEvidenceDisabled}
              />
            </PublicRoutingBackground>
          )}
        />
        <Route
          exact
          path={`/:organisation/open-reporting/messages`}
          render={() => (
            <PublicRoutingBackground>
              <PublicMessageList
                organisation={organisation}
                currentUser={currentUser as unknown as User}
                publicReports={publicReports!}
                messages={messages}
                unreadMessages={unreadMessages}
              />
            </PublicRoutingBackground>
          )}
        />

        <Route
          exact
          path={`/:organisation/open-reporting/messages/:id`}
          render={(props) => (
            <PublicRoutingBackground>
              <ChatContainer
                organisation={organisation}
                currentUser={currentUser as unknown as User}
                publicReports={publicReports!}
                messages={messages}
                reportId={props.match.params.id}
                sendMessage={(reportId, text) =>
                  onMessageSend(reportId, text, session)
                }
                readMessages={(reportId) =>
                  onReadMessages(reportId, messages, session)
                }
                translateMessages={Boolean(translateMessages)}
                updateTranslateMessages={updateTranslateMessages}
                isDynamicTranslationsEnabled={isDynamicTranslationsEnabled}
              />
            </PublicRoutingBackground>
          )}
        />

        <Route
          exact
          path={`/:organisation/open-reporting/change-password`}
          render={() => (
            <PublicRoutingBackground>
              <ChangePassword
                currentUser={currentUser as unknown as User}
                organisation={organisation}
              />
            </PublicRoutingBackground>
          )}
        />

        {isDynamicTranslationsEnabled && (
          <Route
            exact
            path={`/:organisation/open-reporting/message-settings`}
            render={() => (
              <PublicRoutingBackground>
                <MessageSettings
                  organisation={organisation}
                  currentUser={currentUser as unknown as User}
                  updateTranslationEnabled={updateTranslationEnabled}
                />
              </PublicRoutingBackground>
            )}
          />
        )}

        <Route
          exact
          path={`/:organisation/open-reporting/more-evidence/:id`}
          render={(props) => (
            <ReportViewProvider organisation={organisation}>
              <PublicRoutingBackground>
                <PublicMoreEvidence
                  organisation={organisation}
                  currentUser={currentUser as unknown as User}
                  reportId={props.match.params.id}
                  publicReports={publicReports!}
                  isReporterViewEvidenceDisabled={
                    isReporterViewEvidenceDisabled
                  }
                />
              </PublicRoutingBackground>
            </ReportViewProvider>
          )}
        />
      </AuthenticatedRoute>
      <Route
        exact
        path={`/:organisation/open-reporting/`}
        render={() => (
          <PublicRoutingBackground>
            <PageNotFound />
          </PublicRoutingBackground>
        )}
      />
    </Switch>
  );
};
